import { Eye, Receipt, X } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useEstimatorInvoices, type InvoiceRef } from '../lib/estimatorInvoices';

/**
 * EL CHECK "INCLUDE IN THE ESTIMATE" (dueño 2026-10-07): va en cada ítem con archivo de Info pending, Contractor
 * invoices y Contractor documents, en las tarjetas de Review Estimate y First Estimate. Marcarlo le pone al
 * estimador la instrucción de agregar ese invoice al estimado (tarea de la orden y guía).
 */
export function EstimatorInvoiceCheck({ claimId, item }: { claimId: string; item: InvoiceRef }) {
  const { t } = useI18n();
  const { list, marcado, toggle } = useEstimatorInvoices(claimId);
  const on = marcado(item.kind, item.id);
  return (
    <label title={t('ei.includeHint')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 'var(--fs-2xs)', fontWeight: on ? 700 : 500, color: on ? 'var(--accent)' : 'var(--muted)', cursor: 'pointer', border: `1px solid ${on ? 'var(--accent)' : 'var(--line)'}`, borderRadius: 999, padding: '2px 8px', background: on ? 'var(--accent-soft)' : 'transparent' }}>
      <input type="checkbox" checked={on} disabled={list == null} onChange={(e) => void toggle(item, e.target.checked)} />
      {t('ei.include')}
    </label>
  );
}

const usd = (c: number) => `$${(c / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Lo marcado, como instrucción visible en la tarjeta (y lo mismo que va en la tarea y en la guía). */
export function EstimatorInvoicesList({ claimId }: { claimId: string }) {
  const { t } = useI18n();
  const { list, toggle } = useEstimatorInvoices(claimId);
  if (!list?.length) return null;
  const abrir = async (fileId: string) => { try { const r = await api.get<{ url: string }>(`/api/files/${fileId}/url`); window.open(r.url, '_blank', 'noopener'); } catch { /* sin url */ } };
  return (
    <div style={{ border: '1px solid var(--accent)', background: 'var(--accent-soft)', borderRadius: 10, padding: '8px 10px', display: 'grid', gap: 4 }}>
      <div style={{ fontSize: 'var(--fs-2xs)', fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        <Receipt size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('ei.listTitle')}
      </div>
      {list.map((inv) => (
        <div key={`${inv.kind}:${inv.id}`} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 'var(--fs-xs)', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600 }}>{inv.label}</span>
          {inv.amount_cents != null && <span style={{ fontFamily: 'var(--font-mono)' }}>{usd(inv.amount_cents)}</span>}
          {inv.file_name && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{inv.file_name}</span>}
          <span style={{ flex: 1 }} />
          {inv.file_id && <button type="button" className="btn ghost sm" onClick={() => void abrir(inv.file_id!)}><Eye size={12} strokeWidth={1.75} /> {t('ei.view')}</button>}
          <button type="button" className="btn ghost sm" style={{ color: 'var(--red)' }} title={t('ei.remove')} onClick={() => void toggle(inv, false)}><X size={12} strokeWidth={1.75} /></button>
        </div>
      ))}
    </div>
  );
}
